import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { Button } from "simplydo/react/ui/button";
import {
  Checkbox,
  CheckboxContent,
  CheckboxHeader,
} from "simplydo/react/ui/checkbox";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { Input } from "simplydo/react/ui/input";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import ConfigurableTable from "components/lib/ConfigurableTable";
import { useTableSelection } from "components/lib/ConfigurableTable/useTableSelection";
import { LargeCheckbox } from "components/lib/UI";

type AssessmentRound = NonNullable<
  NonNullable<OpenAPI.Schemas["IdeaTemplate"]["assessments"]>["rounds"]
>[number];

export type AssessmentReportFilters = {
  projectLanes: string[];
  labels: string[];
};

type AssessmentReportModalProps = {
  isOpen: boolean;
  assessmentScoreProcessing?: boolean;
  generateAssessmentReport: (
    assessmentRounds: AssessmentRound[],
    includeNotes: boolean,
    selectedFields: string[],
    filters: AssessmentReportFilters,
  ) => void;
  onClose: () => void;
  challenge: OpenAPI.GET<"/challenges/{id}">["response"];
};

const templateFieldTypes = [
  { key: "shorttext", name: "Short text (one line)" },
  { key: "longtext", name: "Long text (paragraphs)" },
  { key: "richtext", name: "Rich text" },
  { key: "numerictext", name: "Numerical text" },
  { key: "slider", name: "Slider" },
  { key: "dropdown", name: "Dropdown box" },
  { key: "optionMultiple", name: "Multiple selection" },
];

type FieldItem = {
  sectionName: string;
  field: { id: string; title?: string; type?: string };
};

const BusinessProfileFields: FieldItem[] = [
  {
    sectionName: "Organisation profile",
    field: { id: "name", title: "Organisation Name" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "phoneNumber", title: "Phone number" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "websiteUrl", title: "Website URL" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "description", title: "Description" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "address.streetAddress", title: "Street address" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "address.country", title: "Country" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "address.region", title: "Region" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "address.city", title: "City" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "address.postcode", title: "Postcode" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "size", title: "Organisation size" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "type", title: "Organisation type" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "foundingYear", title: "Founding year" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "status", title: "Organisation status" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "creditsafeRegNo", title: "Organisation registration number" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "vatNo", title: "VAT registration number" },
  },
];

export const AssessmentReportModal = ({
  isOpen,
  assessmentScoreProcessing = false,
  generateAssessmentReport,
  onClose,
  challenge,
}: AssessmentReportModalProps) => {
  const [activePanel, setActivePanel] = useState<string | null>(null);

  const [includeNotes, setIncludeNotes] = useState<boolean>(false);
  const fieldSelection = useTableSelection<FieldItem>({
    getKey: (item) => item.field.id,
    selectAll: false,
  });
  const selectedFields = fieldSelection.keys;
  const [selectedProjectLanes, setSelectedProjectLanes] = useState<string[]>(
    [],
  );
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFieldType, setSearchFieldType] = useState("");
  const { t } = useTranslation();
  const user = useCurrentUser();

  const template = challenge?.ownerIdeaTemplate;
  const assessmentRounds = useMemo(
    () => template?.assessments?.rounds || [],
    [template?.assessments?.rounds],
  );
  const defaultLaneName =
    challenge?.projectBoard?.defaultLane?.name ?? "Default";

  const labelQuery = useQuery(initUseQuery(api.challenges.getLabels, []));

  const projectLanesQuery = useQuery(
    initUseQuery(api.boards.getProjectLanes, [challenge._id], {
      isEnabled: isOpen && !!challenge._id,
    }),
  );

  const projectLaneOptions = useMemo(() => {
    const lanes = projectLanesQuery.data?.lanes || [];
    return [
      { key: "default", value: "default", text: defaultLaneName },
      ...lanes.map((lane) => ({
        key: lane._id,
        value: lane._id,
        text: lane.name,
      })),
    ];
  }, [defaultLaneName, projectLanesQuery.data?.lanes]);

  const [selectedAssessmentRoundIds, setSelectedAssessmentRoundIds] = useState<
    string[]
  >(() => (assessmentRounds[0]?.id ? [assessmentRounds[0].id] : []));

  const selectedAssessmentRounds = useMemo(
    () =>
      assessmentRounds.filter((round) =>
        selectedAssessmentRoundIds.includes(round.id),
      ),
    [assessmentRounds, selectedAssessmentRoundIds],
  );

  const toggleAssessmentRound = (roundId: string) => {
    setSelectedAssessmentRoundIds((prev) =>
      prev.includes(roundId)
        ? prev.filter((id) => id !== roundId)
        : [...prev, roundId],
    );
  };

  const businessProfileSection = challenge?.ownerIdeaTemplate?.body?.find(
    (section) => section.type === "businessProfile",
  );

  const canViewEdi = util.hasPermission(
    user,
    "org.viewEdi",
    challenge?.organisation,
  );
  const flattenedFields = useMemo(
    () =>
      (
        template?.body
          ?.filter(
            (section) =>
              section.type === "custom" ||
              (section.type === "edi" && canViewEdi) ||
              !section.type,
          )
          .flatMap((section) =>
            (section.fields || []).map((field) => {
              return {
                sectionName: section.name || "Untitled section",
                field: field,
              };
            }),
          ) as FieldItem[]
      ).concat(businessProfileSection ? BusinessProfileFields : []) || [],
    [businessProfileSection, canViewEdi, template?.body],
  );

  const filteredFields = useMemo(() => {
    return flattenedFields.filter((item) => {
      const matchesSearch = item.sectionName
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesType = searchFieldType
        ? item.field.type === searchFieldType
        : true;

      return matchesSearch && matchesType;
    });
  }, [flattenedFields, searchTerm, searchFieldType]);

  return (
    <Modal open={isOpen}>
      <ModalHeader>Generate assessment report</ModalHeader>
      <ModalContent scrolling>
        <p>
          Download an Excel report of all assessments from this{" "}
          {t("generic.challenge")}.
        </p>
        {assessmentScoreProcessing ? (
          <Message color="orange" style={{ marginBottom: 15 }}>
            <MessageHeader>
              {t("assessments.report.processingWarning.header")}
            </MessageHeader>
            <MessageContent>
              {t("assessments.report.processingWarning.body")}
            </MessageContent>
          </Message>
        ) : null}
        {assessmentRounds.length > 0 && (
          <>
            <p style={{ marginBottom: 8 }}>
              Select one or more assessment rounds to include.
            </p>
            <div role="group" aria-label="Assessment rounds">
              <Table compact unstackable>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell collapsing />
                    <TableHeaderCell>Round name</TableHeaderCell>
                    <TableHeaderCell collapsing>Round weight</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assessmentRounds.map((round) => {
                    const selected = selectedAssessmentRoundIds.includes(
                      round.id,
                    );
                    return (
                      <TableRow
                        key={round.id}
                        onClick={() => toggleAssessmentRound(round.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <TableCell collapsing>
                          <LargeCheckbox
                            checked={selected}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleAssessmentRound(round.id);
                            }}
                          />
                        </TableCell>
                        <TableCell>{round.name}</TableCell>
                        <TableCell collapsing>
                          {typeof round.weight === "number"
                            ? `${round.weight}%`
                            : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <Divider hidden />
          </>
        )}
        <Checkbox
          toggle
          checked={includeNotes}
          onChange={(_e, d) => setIncludeNotes(d.checked)}
        >
          <CheckboxHeader>Include assessor notes</CheckboxHeader>
          <CheckboxContent>
            Include any notes the assessor left for each of their assessments.
          </CheckboxContent>
        </Checkbox>
        <Divider hidden />
        <Accordion styled fluid>
          <AccordionTitle
            active={activePanel === "ideaFields"}
            onClick={() =>
              setActivePanel(activePanel === "ideaFields" ? null : "ideaFields")
            }
          >
            Include {t("generic.idea")} fields
          </AccordionTitle>
          <AccordionContent active={activePanel === "ideaFields"}>
            <p>
              Select the {t("generic.idea")} fields you want to include in the
              report.
            </p>
            <div style={{ display: "flex", gap: "1px", marginBottom: "10px" }}>
              <div style={{ marginBottom: "5px", flex: 1, minWidth: "200px" }}>
                <Input
                  placeholder="Search by section name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div style={{ marginBottom: "5px", minWidth: "200px" }}>
                <Dropdown
                  placeholder="Filter by field type"
                  clearable
                  variant="outline"
                  options={templateFieldTypes.map((type) => ({
                    key: type.key,
                    value: type.key,
                    text: type.name,
                  }))}
                  value={searchFieldType}
                  onChange={(_e, { value }) =>
                    setSearchFieldType(value as string)
                  }
                />
              </div>
            </div>
            <ConfigurableTable
              containerStyle={{ maxHeight: 400, overflowY: "auto" }}
              tableKey="challengeSectionFieldImport"
              selection={fieldSelection}
              columns={[
                { key: "sectionName", name: "Section" },
                {
                  key: "fieldTitle",
                  name: "Field",
                  render: ({ item }) => (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <p style={{ margin: 0 }}>
                        {item.field.type === "row"
                          ? "Row divider"
                          : item.field.type === "infotext"
                            ? "Information text"
                            : item.field.title}
                      </p>
                    </div>
                  ),
                },
              ]}
              data={filteredFields}
            />
          </AccordionContent>
          <AccordionTitle
            active={activePanel === "ideaFilters"}
            onClick={() =>
              setActivePanel(
                activePanel === "ideaFilters" ? null : "ideaFilters",
              )
            }
          >
            {t("common:capitalise", { key: "generic.idea" })} filters
          </AccordionTitle>
          <AccordionContent active={activePanel === "ideaFilters"}>
            <p>
              Optionally limit the report to {t("generic.ideas")} on selected
              project lanes or with selected labels. Leave both empty to include
              all assessed {t("generic.ideas")}.
            </p>
            <div style={{ marginBottom: 15 }}>
              <label
                style={{ display: "block", marginBottom: 5, fontWeight: 600 }}
              >
                Project lanes
              </label>
              <Dropdown
                upward
                multiple
                search
                variant="outline"
                clearable
                placeholder="Select project lanes..."
                options={projectLaneOptions}
                value={selectedProjectLanes}
                onChange={(_e, { value }) =>
                  setSelectedProjectLanes((value as string[]) || [])
                }
              />
            </div>
            {labelQuery?.data?.labels && labelQuery.data.labels.length > 0 && (
              <div style={{ marginBottom: 15 }}>
                <label
                  style={{ display: "block", marginBottom: 5, fontWeight: 600 }}
                >
                  Labels
                </label>
                <Dropdown
                  upward
                  multiple
                  search
                  variant="outline"
                  clearable
                  placeholder="Select labels..."
                  options={labelQuery.data.labels.map((label) => ({
                    key: label,
                    value: label,
                    text: label,
                  }))}
                  value={selectedLabels}
                  onChange={(_e, { value }) =>
                    setSelectedLabels((value as string[]) || [])
                  }
                />
              </div>
            )}
          </AccordionContent>
        </Accordion>
      </ModalContent>
      <ModalActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button
          disabled={
            assessmentRounds.length > 0 && selectedAssessmentRounds.length === 0
          }
          onClick={() =>
            generateAssessmentReport(
              selectedAssessmentRounds,
              includeNotes,
              selectedFields,
              { projectLanes: selectedProjectLanes, labels: selectedLabels },
            )
          }
          color="primary"
        >
          Generate Report
        </Button>
      </ModalActions>
    </Modal>
  );
};
